import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Local edit buffer over a server value.
 *
 * Server updates are only adopted while the form is clean, so a background
 * refetch can never overwrite what an editor is halfway through typing.
 */
export function useDraft<T>(serverValue: T | undefined) {
  const [draft, setDraft] = useState<T | undefined>(serverValue);
  const baseline = useRef<string>(JSON.stringify(serverValue ?? null));

  useEffect(() => {
    const incoming = JSON.stringify(serverValue ?? null);
    const dirty = JSON.stringify(draft ?? null) !== baseline.current;
    if (incoming !== baseline.current && !dirty) {
      baseline.current = incoming;
      setDraft(serverValue);
    }
    // `draft` is deliberately excluded: this effect reacts to server changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverValue]);

  const dirty = useMemo(
    () => JSON.stringify(draft ?? null) !== baseline.current,
    [draft],
  );

  /** Call after a successful save so the saved value becomes the new baseline. */
  const commit = (saved: T) => {
    baseline.current = JSON.stringify(saved);
    setDraft(saved);
  };

  const reset = () => setDraft(JSON.parse(baseline.current) as T);

  return { draft, setDraft, dirty, commit, reset };
}

/** Warns before a tab close or reload would discard unsaved edits. */
export function useUnsavedWarning(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
}
